import { clamp, damp } from '../math/mathx.js';
import { range, streamRng } from '../math/rng.js';
import { newShot, rollShot } from '../systems/accuracy.js';
import {
  AI_DEFAULTS,
  BRAIN,
  PED,
  PED_TUNING,
  SpatialHash,
  STATE,
  TAU,
  TEAM,
  leashCheck,
  mergeTuning,
  newFsmOut,
  newPedOut,
  seek,
  stepFSM,
  stepHomeFSM,
  stepPedFSM,
  wanderDir,
} from './ai.js';

/**
 * @file The enemy STORE: a struct-of-arrays for up to `cap` enemies, driven on
 * the fixed 60 Hz step, rendered by either backend from the same buffers.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY SoA AND NOT `class Enemy`
 * ─────────────────────────────────────────────────────────────────────────────
 * 120 enemies x 60 steps/s is 7 200 updates a second. An array of objects means
 * 7 200 pointer chases through cold memory, and the two renderers would then
 * have to walk that same pointer graph AGAIN to fill their instance buffers. In
 * SoA form the update is a linear scan over `Float32Array`s, the spatial hash is
 * a counting sort over the same arrays, and both renderers read the buffers
 * directly. Nothing is allocated per enemy, per step, ever.
 *
 * ⚠ There are exactly TWO allocations in {@link EnemyManager#update} and both
 * are deliberate:
 *  1. `CollisionGrid.resolveCircle()` returns a fresh `{x,z,hit,nx,nz}` record —
 *     that is T3's API (shared with `Player`), and it is only called for enemies
 *     that actually MOVED this step.
 *  2. The `'player:damage'` payload. Attacks are rare (~1 per enemy per 1.1 s)
 *     and a reused payload object would be a trap for any bus subscriber that
 *     keeps the reference.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WALK WOBBLE IS INLINED
 * ─────────────────────────────────────────────────────────────────────────────
 * `actors/WalkWobble.js` is ~10 floats and a `damp()` per actor. Instantiating
 * 512 of them would put 512 objects back into the design SoA exists to remove,
 * so the same maths runs here over `animPhase` / `wobbleAmp` /
 * `wobbleTilt` / `wobbleBob`. The formulas are IDENTICAL (bob at 2x the tilt
 * rate, amplitude — not phase — damped to zero on stop); if you change one,
 * change both.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TEAMS: ONE POOL, TWO ALLEGIANCES
 * ─────────────────────────────────────────────────────────────────────────────
 * Squad mode (HANDOFF Problem 4) puts the player's units and the wave enemies in
 * the SAME manager, separated only by {@link EnemyManager#team}. One pool was
 * chosen over two because everything expensive here is a neighbourhood query:
 * with one pool, separation already pushes an ally off an enemy, the spatial
 * hash is built once, and "nearest opposing unit" is a scan over arrays that are
 * already hot. Two pools would have needed cross-pool separation, cross-pool
 * targeting and two of every buffer, to save one byte per slot.
 *
 * ⚠ HERO MODE IS THE DEFAULT AND MUST STAY BIT-IDENTICAL. Everything team-aware
 * hangs off `ctx.targeting === 'teams'`; without it not one branch in the step
 * changes, not one extra rng number is drawn, and `team` / `target` / `home`
 * are never read. `tests/squad.test.mjs` pins that with a checksum of a
 * 400-step hero fight — if you touch the step, run it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FACTIONS: THE SAME BYTE, A BIGGER TABLE
 * ─────────────────────────────────────────────────────────────────────────────
 * `ctx.targeting: 'factions'` reuses `team` as a `game/factions.js#FACTION` id
 * and asks `ctx.hostile(a, b)` instead of comparing bytes. Nothing was added to
 * the layout, nothing changed for `'teams'`, and hero mode still cannot reach
 * any of it. Three faction-only behaviours:
 *  • the PLAYER is a candidate target for a faction `ctx.playerHostile()`
 *    approves, and the unit fights whichever of (nearest hostile slot, player)
 *    is CLOSER. Without that comparison a gang member with a rival 60 m away
 *    walks past the player who just shot him;
 *  • a slot's {@link BRAIN} may take it out of the fighter step entirely —
 *    `CIVILIAN` runs {@link _stepPed}, `SEATED` runs {@link _stepSeated};
 *  • {@link damage} carries an ATTACKER FACTION so the caller can tell "a stray
 *    round from the greens" from "the player shot me". It is a trailing
 *    optional argument, so every existing call site keeps its exact meaning.
 *
 * The two team-mode behaviours worth knowing before you edit anything:
 *  • A unit's TYPE decides how it fights. No `fire` stat => the melee FSM. A
 *    `fire` stat => it never enters ATTACK, plants at `fire.range * 0.9` and the
 *    manager applies the damage on a per-slot accumulator (`onFire` is the
 *    visuals hook, fired AFTER the damage has already landed). With the OPT-IN
 *    {@link STANCE} it also STOPS FOR A BEAT to shoot instead of firing on the
 *    move — that halt is the player's advantage, since the player never has to.
 *  • A unit with a HOME (a formation slot) runs `stepHomeFSM` instead, and is
 *    leashed to `tune.leash` metres from it. That leash is the whole reason a
 *    formation reads as a formation; see `ai.js#leashCheck` for the hysteresis.
 *
 * @module engine/actors/EnemyManager
 */

/** @typedef {import('../types.js').EnemyType} EnemyType */
/** @typedef {import('../types.js').Rng} Rng */
/** @typedef {import('./ai.js').AiTuning} AiTuning */

/**
 * Everything the update needs from the outside world. All fields optional —
 * a manager with an empty ctx still runs (that is how the unit tests drive it).
 * @typedef {Object} EnemyUpdateCtx
 * @property {{position: {x: number, y: number, z: number}, radius?: number}} [player]
 *   THE target in `'player'` targeting. May be omitted entirely in `'teams'`.
 * @property {{resolveCircle: Function, blocked?: Function}} [collision]
 * @property {(x: number, z: number) => number} [heightAt]
 * @property {import('../types.js').LightProbeGrid|null} [probes]
 * @property {Rng} [rng] Overrides the manager's own stream.
 * @property {import('../types.js').EventBus} [bus] For `'player:damage'`.
 * @property {'player'|'teams'|'factions'} [targeting='player'] `'player'` is
 *   hero mode and the default: every unit chases `ctx.player` and hits it
 *   through the bus. `'teams'` is the two-allegiance mode: every unit chases
 *   the nearest LIVE unit of the opposing {@link TEAM} and damages it
 *   slot-to-slot, with no bus traffic. `'factions'` is `'teams'` plus three
 *   things — the `team` byte is a FACTION id resolved through {@link hostile}
 *   rather than "not mine", a faction that {@link playerHostile} approves may
 *   ALSO fight `ctx.player` (whichever is nearer), and slots carrying a
 *   {@link BRAIN} other than `NONE` run their own step instead of the fighter
 *   one.
 * @property {((a: number, b: number) => boolean)|null} [hostile] FACTION MODE.
 *   Replaces the built-in "different team" test. Must be pure and symmetric;
 *   it is called O(pool) times per acquisition scan.
 * @property {((faction: number) => boolean)|null} [playerHostile] FACTION MODE.
 *   Does this faction want to kill `ctx.player` right now?
 */

/** Walk-cycle defaults, mirroring `WalkWobble`'s constructor. */
const WOBBLE = Object.freeze({
  freq: 9,
  tilt: 0.1,
  bob: 0.045,
  damp: 12,
  speedFreq: 0.45,
});

/**
 * THE STANCE IS A DUTY CYCLE, NOT A DURATION.
 *
 * A ranged NPC used to shoot WHILE CLOSING, which left the player with no
 * advantage of their own: the player shoots on the move, so an NPC that does
 * the same is simply a better player. The stance makes an NPC STOP for a beat
 * to shoot — `braceFrac` of a cycle spent planted BEFORE the round leaves
 * (that is the telegraph, and the player's kiting window), `recoverFrac`
 * planted after it.
 *
 * ⚠⚠ BOTH NUMBERS ARE FRACTIONS OF THE WEAPON'S OWN `fire.interval`, NEVER
 * TABLED SECONDS. A plausible tabled `0.35 + 0.45 = 0.80 s` is LONGER than
 * `swat`'s entire 0.55 s cycle: swat would plant the moment it entered
 * `fire.range` (13 m) and never close to its 11.7 m standoff — which silently
 * re-opens the 2026-08-07 combat bug from the other side, every hostile parked
 * at the EDGE of the player's reach instead of just outside it.
 * `tests/shooting.test.mjs` section (g) carries that as a negative control.
 *
 * ⚠ `braceFrac + recoverFrac < 1` IS A MODULE INVARIANT, and it is what makes
 * the feature interval-independent: every ranged unit at ANY authored interval
 * still spends 45% of every cycle moving, and therefore still closes to
 * `fire.range * 0.9`. There is no cast-derived constant to re-check when a def
 * changes its cadence — unlike `FIRE.range` and `bulletToCar`, which are.
 *
 * ⚠ DPS IS UNCHANGED. The commit still consumes `fireAcc -= interval` and the
 * accumulator keeps running through the brace; the round simply lands `brace`
 * later. The cadence is PHASE-SHIFTED, not thinned — THE HALT COSTS THE NPC
 * GROUND, NOT DAMAGE.
 *
 * Over the shipped cast (planted / free, seconds per cycle): the nine gang
 * members 0.66/0.54, `police_officer` 0.495/0.405, `swat` 0.30/0.25, `tank`
 * 0.99/0.81.
 */
export const STANCE = Object.freeze({ braceFrac: 0.3, recoverFrac: 0.25 });

/** Fallback when a type omits a field. */
const TYPE_FALLBACK = Object.freeze({
  radius: 0.45,
  speed: 3.2,
  hp: 30,
  damage: 8,
  height: 1.4,
  worldHeight: 1.4,
  aggroRange: 0,
  blobR: 0,
});

export class EnemyManager {
  /**
   * @param {Object} [o]
   * @param {EnemyType[]} [o.types] Type table; `typeIdx` indexes it. Duck-typed:
   *   anything with `{radius, speed, hp, damage?, height?}` works (tests pass
   *   plain objects).
   * @param {number} [o.cap=512] Hard slot cap. Buffers are allocated up front.
   * @param {string|number} [o.seed='enemies'] Drives wander headings + dwell
   *   times. Same seed + same inputs => same positions, forever.
   * @param {Partial<AiTuning>|null} [o.tune] Overrides {@link AI_DEFAULTS}.
   * @param {Object|null} [o.wobble] Overrides the inlined walk cycle.
   * @param {number} [o.probeMs=300] Rolling window over which EVERY live enemy
   *   gets its {@link import('../bake/LightProbeGrid.js').LightProbeGrid} tint
   *   refreshed. 300 ms is imperceptible for a light that only changes when the
   *   actor walks into an alley.
   * @param {number} [o.hashCell=3] Separation bucket size, metres.
   * @param {number} [o.retargetMs=250] SQUAD MODE ONLY. Rolling window over
   *   which every live unit re-picks its nearest opponent. A unit whose target
   *   DIES re-picks immediately regardless — this window only covers the slower
   *   "something closer walked past me" case, and it is what keeps target
   *   acquisition off the O(n²)-per-step path.
   * @param {import('../systems/accuracy.js').AccuracySpec|null} [o.accuracy]
   *   OPT-IN, and null by default on purpose. When set, every ranged shot rolls
   *   against the distance-based miss curve in `systems/accuracy.js` and a miss
   *   applies no damage (the `onFire` report carries the scatter point so the
   *   tracer can fly at it). Left null, this class draws ZERO extra numbers and
   *   behaves exactly as it did — which is what keeps the hero golden checksum
   *   in `tests/squad.test.mjs` valid. Squad mode opts in; hero mode's own
   *   auto-attack rolls in `systems/Gameplay.js` instead, outside this class.
   * @param {{braceFrac?: number, recoverFrac?: number}|boolean|null} [o.stance]
   *   OPT-IN on the `accuracy` precedent, and null by default for the same
   *   reason: with it unset {@link _stepFire} and {@link _stepFirePlayer} are
   *   BIT-IDENTICAL to the shipped path, so the hero golden checksum in
   *   `tests/hero.test.mjs` cannot move BY CONSTRUCTION rather than by
   *   argument — and that suite's teams-mode `FireSpec` sections, which build
   *   managers without a stance, need no edits either.
   *
   *   Set it and a ranged unit STOPS FOR A BEAT TO SHOOT — see {@link STANCE}
   *   for what the two fractions mean and why they are fractions. `true` takes
   *   the shipped duty cycle; an object overrides either fraction; a
   *   per-`FireSpec` `braceFrac`/`recoverFrac` overrides both. Live-settable
   *   (`manager.stance = true`) — nothing caches it.
   */
  constructor(o = {}) {
    const {
      types = [],
      cap = 512,
      seed = 'enemies',
      tune = null,
      wobble = null,
      probeMs = 300,
      hashCell = 3,
      retargetMs = 250,
      accuracy = null,
      stance = null,
    } = o;

    if (!(cap > 0)) throw new Error('EnemyManager: `cap` must be > 0');

    /** @type {number} */
    this.cap = cap | 0;
    /** @type {EnemyType[]} */
    this.types = types;
    /** @type {AiTuning} */
    this.tune = mergeTuning(tune);
    /** @type {typeof WOBBLE} */
    this.wobble = wobble ? { ...WOBBLE, ...wobble } : WOBBLE;
    /** @type {Rng} */
    this.rng = streamRng(seed, 'enemies');
    /**
     * @type {import('../systems/accuracy.js').AccuracySpec|null} See the ctor
     * option. Live-settable — nothing caches it.
     */
    this.accuracy = accuracy;
    /**
     * @type {{braceFrac?: number, recoverFrac?: number}|boolean|null} See the
     * ctor option and {@link STANCE}. Stored VERBATIM: the fractions are read
     * through a `??` chain (`fire` → this → {@link STANCE}), so a bare `true`
     * resolves to the shipped duty cycle without a normalisation step that
     * could disagree with the table. Live-settable.
     */
    this.stance = stance;
    /**
     * @type {Rng} A SEPARATE stream for aim rolls. It must never be `this.rng`:
     * the hero checksum pins that stream's draw order, and a miss draws four
     * numbers where a hit draws one.
     */
    this.aimRng = streamRng(seed, 'aim');

    // ── SoA ──────────────────────────────────────────────────────────────────
    const n = this.cap;
    /** World X. */ this.x = new Float32Array(n);
    /** Ground height at (x,z) — the FEET, like `Player.position.y`. */
    this.y = new Float32Array(n);
    /** World Z. */ this.z = new Float32Array(n);
    /** Velocity. */ this.vx = new Float32Array(n);
    this.vz = new Float32Array(n);
    this.hp = new Float32Array(n);
    this.maxHp = new Float32Array(n);
    /** Seconds in the current {@link STATE}. */ this.timer = new Float32Array(n);
    /** 0..1, decays over `flashDecay`. */ this.flash = new Float32Array(n);
    /** Render scale multiplier (1, shrinking to 0 while DYING). */
    this.scale = new Float32Array(n);
    /** Walk-cycle phase, radians 0..TAU — also drives the sprite atlas column. */
    this.animPhase = new Float32Array(n);
    /** Damped walk-cycle amplitude, 0..1 (inlined `WalkWobble.amp`). */
    this.wobbleAmp = new Float32Array(n);
    /** Screen-space lean, radians -> `SpriteBatch.set(..., {rot})`. */
    this.wobbleTilt = new Float32Array(n);
    /** Vertical rise, metres -> added to `y` at render time. */
    this.wobbleBob = new Float32Array(n);
    /** Per-slot collision radius (copied from the type on spawn). */
    this.radius = new Float32Array(n);
    /** Current wander heading, radians. */
    this.wanderA = new Float32Array(n);
    /** Randomised IDLE/WANDER dwell, seconds. */
    this.dwell = new Float32Array(n);
    /**
     * PER-SLOT SPEED SCALE, on top of the type's authored `speed`. 1 for every
     * spawn, which is why this array is inert: `maxSpeed * 1` is bit-identical
     * to `maxSpeed` in IEEE-754, so the hero path's arithmetic — and the golden
     * checksum over it — cannot move because this exists.
     *
     * It is here because SPEED IS AUTHORED PER TYPE and a mission needs it per
     * ACTOR: a chase target running at the def's 3.2 m/s in front of a 6.5 m/s
     * player is not a chase, it is a walk. {@link setSpeedMul} is the only
     * writer, and `spawn()` resets it — a recycled slot must never inherit
     * somebody else's legs.
     * @type {Float32Array}
     */
    this.speedMul = new Float32Array(n).fill(1);

    /** @type {Uint8Array} {@link STATE}. `DEAD` also means "slot is free". */
    this.state = new Uint8Array(n).fill(STATE.DEAD);
    /** @type {Uint8Array} Index into {@link types}. */
    this.typeIdx = new Uint8Array(n);
    /**
     * Sticky heading, quantised to 1/256 turn (~1.4 degrees). A byte is plenty
     * for a 4-row impostor atlas and for facing a mesh along its travel, and
     * being STICKY is the point: an enemy that stops mid-step must not snap to
     * yaw 0 because its velocity hit zero.
     * @type {Uint8Array}
     */
    this.facing = new Uint8Array(n);
    /** @type {Uint8Array} 1 = participates in separation (not DEAD/DYING). */
    this.solid = new Uint8Array(n);

    // ── teams (squad mode; all inert under the default `targeting: 'player'`) ─
    /**
     * Allegiance per slot — see {@link TEAM}. Zeroed, i.e. `TEAM.HOSTILE`, which
     * is exactly what every hero-mode spawn wants without asking.
     * @type {Uint8Array}
     */
    this.team = new Uint8Array(n);
    /**
     * Current target SLOT, or `-1`. In hero mode `-1` is not "no target" — it is
     * "the `ctx.player`", and the array is never written. In teams mode `-1`
     * genuinely means "nobody to fight", and the unit holds its slot or wanders.
     * @type {Int32Array}
     */
    this.target = new Int32Array(n).fill(-1);
    /** Per-slot ranged cooldown accumulator, seconds. @type {Float32Array} */
    this.fireAcc = new Float32Array(n);
    /**
     * Seconds remaining BEFORE the committed round leaves the barrel. 0 = not
     * committed. Only ever non-zero when {@link stance} is set, which is what
     * keeps the frozen paths frozen.
     * @type {Float32Array}
     */
    this.fireBrace = new Float32Array(n);
    /**
     * Seconds remaining of the PLANTED window (it covers the brace AND the
     * recover). It is the ONE thing `update` reads to halt a shooter, and it
     * outlives the brace on purpose: a unit whose target dies mid-brace eases
     * out of the stance instead of snapping into a sprint.
     * @type {Float32Array}
     */
    this.fireHold = new Float32Array(n);
    /** Formation slot anchor X (only meaningful where `hasHome` is 1). */
    this.homeX = new Float32Array(n);
    /** Formation slot anchor Z. */
    this.homeZ = new Float32Array(n);
    /** @type {Uint8Array} 1 = this slot is leashed to (`homeX`, `homeZ`). */
    this.hasHome = new Uint8Array(n);
    /**
     * @type {Uint8Array} 1 = broke its leash and is walking back. Hysteresis
     * state for {@link leashCheck}; readable for debug HUDs, owned by the step.
     */
    this.returning = new Uint8Array(n);

    // ── brains (faction mode; all inert while every byte is BRAIN.NONE) ──────
    /**
     * What KIND of step this slot runs — see {@link BRAIN}. Zeroed, i.e.
     * `BRAIN.NONE`, which is the fighter every existing spawn already is.
     * @type {Uint8Array}
     */
    this.brain = new Uint8Array(n);
    /** Pedestrian mood, {@link PED}. Only read when `brain` is CIVILIAN. */
    this.mood = new Uint8Array(n);
    /**
     * @type {Uint8Array} 1 = this SEATED slot is INSIDE a cabin rather than
     * halfway out of one. NOBODY IS DRAWN INSIDE A CAR (owner decision,
     * playtest round 2 — see `renderers/SpriteEnemyRenderer.js`), so this is
     * the byte that takes an actor off screen: the renderers skip it, and the
     * slot goes on living, riding its seat and taking bullets.
     *
     * It is NOT the same question as `BRAIN.SEATED`: a carjack victim stays
     * SEATED for the whole drag and must be drawn, because by then his body is
     * outside the car. {@link placeSeated}'s last argument is the difference.
     */
    this.inCabin = new Uint8Array(n);
    /** Seconds in the current {@link PED} mood. */
    this.moodT = new Float32Array(n);
    /** Randomised stroll/pause dwell, seconds (the pedestrian's `dwell`). */
    this.moodDwell = new Float32Array(n);
    /**
     * Where a strolling civilian is walking to. The manager does NOT know what
     * a pavement is: the population director writes a waypoint here and reads
     * {@link atGoal} back. That is the whole interface.
     */
    this.goalX = new Float32Array(n);
    this.goalZ = new Float32Array(n);
    /** @type {Uint8Array} 1 = `goalX/goalZ` hold a real waypoint. */
    this.hasGoal = new Uint8Array(n);
    /** Flight bearing, radians — re-jittered every `PED_TUNING.fleeRefresh`. */
    this.fleeA = new Float32Array(n);

    /** Per-slot day/night ambient from the probe grid, refreshed on a rolling
     *  window. Both renderers read these — sampling once here beats sampling
     *  twice, and the tints survive a backend switch. */
    this.tintD = new Float32Array(n * 3).fill(1);
    this.tintN = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      this.tintN[i * 3] = 0.25;
      this.tintN[i * 3 + 1] = 0.3;
      this.tintN[i * 3 + 2] = 0.45;
    }

    // ── free list ────────────────────────────────────────────────────────────
    /** @type {Int32Array} LIFO stack of free slots. */
    this._free = new Int32Array(n);
    for (let i = 0; i < n; i++) this._free[i] = n - 1 - i; // pop order 0,1,2,...
    this._freeN = n;

    /** @type {number} Live enemies. */
    this._alive = 0;
    /** @type {number} High-water mark: renderers iterate `0 .. top-1`. */
    this.top = 0;
    /** @type {Int32Array} Live count per type (the spawner's budget check). */
    this.aliveByType = new Int32Array(256);
    /**
     * @type {Int32Array} Live count per {@link TEAM} — maintained in `spawn` /
     * `_release` exactly like {@link aliveByType}, so {@link aliveOnTeam} is O(1)
     * and the `WaveDirector` can poll it every step without walking the pool.
     */
    this.aliveByTeam = new Int32Array(256);

    /** @type {number} Seconds for a hit flash to fade. */
    this.flashDecay = 0.15;

    /** @type {((i: number, m: EnemyManager) => void)|null} Slot became live. */
    this.onSpawn = null;
    /** @type {((i: number, m: EnemyManager) => void)|null} Slot went free. */
    this.onDespawn = null;
    /**
     * Fired from {@link kill} — so it covers deaths from ANY source, including
     * an external `damage()` call from the hero's auto-fire, not just the ones
     * the step produces. Fires BEFORE the death shrink, i.e. while the victim is
     * still `isLive()`; the slot frees `tune.dyingTime` later through
     * {@link onDespawn}.
     * @type {((victim: number, m: EnemyManager) => void)|null}
     */
    this.onKill = null;
    /**
     * Fired when a ranged unit shoots. The manager has ALREADY resolved the
     * shot — this is the tracer/muzzle/blood hook, nothing more.
     *
     * The fourth argument is the SHOT REPORT (`systems/accuracy.js#Shot`):
     * `hit` says whether damage was applied, and `x`/`z` are where the round
     * actually ended up — the target on a hit, the scatter point on a miss.
     * It is scratch, reused every shot; read it, do not keep it.
     * @type {((i: number, target: number, m: EnemyManager,
     *   shot: import('../systems/accuracy.js').Shot) => void)|null}
     */
    this.onFire = null;
    /**
     * Fired from {@link damage} for EVERY hit that lands, lethal or not, with
     * the attacker's faction (or `-1` when the caller did not say). The faction
     * system listens here to turn a stray round into a feud and to bill the
     * player for a crime; nothing in the step reads it.
     * @type {((victim: number, srcFaction: number, amount: number,
     *   m: EnemyManager) => void)|null}
     */
    this.onDamage = null;
    /**
     * The faction that landed the killing blow, valid DURING {@link onKill}.
     * A field rather than an argument because `kill()` is also called from the
     * step and from scripts, and neither has an attacker to pass.
     * @type {number}
     */
    this.lastAttacker = -1;

    // ── scratch (allocated once) ─────────────────────────────────────────────
    this._hash = new SpatialHash({ cell: hashCell, cap: this.cap });
    this._fsm = newFsmOut();
    this._seek = { x: 0, z: 0, d: 0 };
    this._push = { x: 0, z: 0 };
    this._probePos = { x: 0, z: 0 };
    this._probeD = new Float32Array(3);
    this._probeN = new Float32Array(3);
    this._probeSteps = Math.max(1, Math.round(probeMs / 1000 / (1 / 60)));
    this._probeCursor = 0;
    this._shot = newShot();
    this._seekHome = { x: 0, z: 0, d: 0 };
    this._retargetSteps = Math.max(1, Math.round(retargetMs / 1000 / (1 / 60)));
    this._targetCursor = 0;
    this._ped = newPedOut();
    /**
     * @type {((a: number, b: number) => boolean)|null} Faction hostility, taken
     * from `ctx.hostile` at the top of every `update`. Null => the teams rule
     * ("anything not on my team").
     */
    this._hostileFn = null;
    /**
     * Live opponents per faction, rebuilt once per step when {@link _hostileFn}
     * is set. It replaces `alive - aliveByTeam[mine]` as the cheap "is there
     * anything to look for" gate, which is only correct with two sides.
     * @type {Int32Array}
     */
    this._foeAlive = new Int32Array(256);
    /** Player position cached for `damage()`'s default knockback direction. */
    this._px = 0;
    this._pz = 0;
    /** @type {number} Seconds since construction (wander drift phase). */
    this.elapsed = 0;
    this._disposed = false;
  }

  // ── population ─────────────────────────────────────────────────────────────

  /** @returns {number} Live enemies (DYING counts — it is still on screen). */
  get alive() {
    return this._alive;
  }

  /**
   * @param {number} i
   * @returns {EnemyType|undefined}
   */
  typeOf(i) {
    return this.types[this.typeIdx[i]];
  }

  /**
   * @param {number} i
   * @returns {number} Heading in radians, 0..TAU, from the sticky facing byte.
   */
  yawOf(i) {
    return (this.facing[i] / 256) * TAU;
  }

  /**
   * @param {number} team {@link TEAM}
   * @returns {number} Live units on that team, DYING included (it is still on
   *   screen and still blocking a "wave cleared" check). O(1).
   */
  aliveOnTeam(team) {
    return this.aliveByTeam[team & 255];
  }

  /**
   * Leashes a slot to a formation position. The unit walks back to it whenever
   * it is not engaged, holds there without wandering, and breaks off any pursuit
   * that drags it more than `tune.leash` metres away.
   *
   * Squad mode only — a home is never read under `targeting: 'player'`.
   *
   * @param {number} i
   * @param {number} x @param {number} z
   */
  setHome(i, x, z) {
    if (i < 0 || i >= this.cap) return;
    this.homeX[i] = x;
    this.homeZ[i] = z;
    this.hasHome[i] = 1;
    this.returning[i] = 0;
  }

  /**
   * Scales THIS SLOT's movement speed on top of its type's authored one — see
   * {@link speedMul}. Both step paths (fighter and pedestrian) honour it, so a
   * mission can make one gang member sprint without inventing a second def.
   *
   * ⚠ It scales MOVEMENT ONLY. Attack cadence, fire interval and the walk
   * cycle's own frequency all key off other numbers, so a 2x runner still
   * swings and shoots at the rate its def authored — which is what you want: a
   * target that runs away twice as fast should not also shoot twice as fast.
   *
   * @param {number} i
   * @param {number} k Multiplier. Clamped to `[0, 8]`; 1 restores the def.
   */
  setSpeedMul(i, k) {
    if (i < 0 || i >= this.cap) return;
    this.speedMul[i] = !(k > 0) ? 0 : k > 8 ? 8 : k;
  }

  /**
   * Drops the leash: the unit reverts to wave-enemy behaviour (chase, or wander
   * when it has nothing to chase). `spawn()` already does this for you.
   * @param {number} i
   */
  clearHome(i) {
    if (i < 0 || i >= this.cap) return;
    this.hasHome[i] = 0;
    this.returning[i] = 0;
  }

  /**
   * @param {number} typeIdx Index into {@link types}.
   * @param {number} x @param {number} z World position.
   * @param {number} [y=0] Ground height; the next `update` re-samples it.
   * @param {number} [team=TEAM.HOSTILE] Allegiance. Trailing and defaulted so
   *   every existing 4-argument call site keeps its exact meaning.
   * @returns {number} Slot id, or `-1` when the manager is full.
   */
  spawn(typeIdx, x, z, y = 0, team = TEAM.HOSTILE) {
    if (this._freeN === 0) return -1;
    const t = this.types[typeIdx] ?? TYPE_FALLBACK;
    const i = this._free[--this._freeN];

    this.x[i] = x;
    this.y[i] = y;
    this.z[i] = z;
    this.vx[i] = 0;
    this.vz[i] = 0;
    this.hp[i] = t.hp ?? TYPE_FALLBACK.hp;
    this.maxHp[i] = this.hp[i];
    this.timer[i] = 0;
    this.flash[i] = 0;
    this.scale[i] = 1;
    this.animPhase[i] = this.rng() * TAU; // desynchronised gaits out of the gate
    this.wobbleAmp[i] = 0;
    this.wobbleTilt[i] = 0;
    this.wobbleBob[i] = 0;
    this.radius[i] = t.radius ?? TYPE_FALLBACK.radius;
    this.speedMul[i] = 1;
    this.wanderA[i] = wanderDir(this.rng, this.elapsed);
    this.dwell[i] = range(this.rng, 0.8, 2.6);
    this.state[i] = STATE.IDLE;
    this.typeIdx[i] = typeIdx & 255;
    this.facing[i] = (this.rng() * 256) & 255;
    this.solid[i] = 1;

    // Team state. NOTE the rng draws above are untouched and unreordered: hero
    // mode's stream must stay bit-identical, so nothing here may consume one.
    this.team[i] = team & 255;
    this.target[i] = -1;
    this.fireAcc[i] = 0;
    this.fireBrace[i] = 0;
    this.fireHold[i] = 0;
    this.hasHome[i] = 0;
    this.returning[i] = 0;

    // Brain state. Same rule as the team block above: plain assignments only,
    // never an rng draw — a recycled slot must not carry a stale pedestrian
    // mood or a stale seat into its new life.
    this.brain[i] = BRAIN.NONE;
    this.mood[i] = PED.STROLL;
    this.moodT[i] = 0;
    this.moodDwell[i] = PED_TUNING.strollMin;
    this.hasGoal[i] = 0;
    this.fleeA[i] = 0;
    this.inCabin[i] = 0;

    // Neutral tints until the probe sampler reaches this slot.
    const o3 = i * 3;
    this.tintD[o3] = 1;
    this.tintD[o3 + 1] = 1;
    this.tintD[o3 + 2] = 1;
    this.tintN[o3] = 0.25;
    this.tintN[o3 + 1] = 0.3;
    this.tintN[o3 + 2] = 0.45;

    if (i >= this.top) this.top = i + 1;
    this._alive++;
    this.aliveByType[this.typeIdx[i]]++;
    this.aliveByTeam[this.team[i]]++;
    this.onSpawn?.(i, this);
    return i;
  }

  /**
   * Applies damage: flash, knockback stagger, and death when hp runs out.
   *
   * @param {number} i
   * @param {number} amt Damage. <= 0 is ignored (heal through `hp` directly).
   * @param {number} [kx] Knockback direction X (need not be normalised).
   *   Defaults to "away from the player's last known position".
   * @param {number} [kz]
   * @param {number} [src=-1] ATTACKER FACTION (`game/factions.js#FACTION`, or
   *   `PLAYER_FACTION`). Trailing and defaulted, so every existing 2–4 argument
   *   call site keeps its exact meaning. It is not used by the step at all — it
   *   is reported through {@link onDamage} / {@link lastAttacker} so the faction
   *   system can turn a stray round into a feud.
   * @returns {boolean} True when this hit KILLED it (started the death shrink).
   */
  damage(i, amt, kx, kz, src = -1) {
    if (!this.isLive(i) || this.state[i] === STATE.DYING) return false;
    if (!(amt > 0)) return false;

    this.hp[i] -= amt;
    this.flash[i] = 1;

    // Being shot is the one scare a civilian cannot miss. Inert in hero mode:
    // `brain` is zero for every spawn that never asked for a pedestrian.
    if (this.brain[i] === BRAIN.CIVILIAN) this.scare(i, this.x[i] - (kx ?? 0), this.z[i] - (kz ?? 0));

    this.onDamage?.(i, src, amt, this);

    if (this.hp[i] <= 0) {
      this.hp[i] = 0;
      this.kill(i, src);
      return true;
    }

    // Knockback: away from the source, or from the player when unspecified.
    let dx = kx ?? this.x[i] - this._px;
    let dz = kz ?? this.z[i] - this._pz;
    const l = Math.hypot(dx, dz);
    if (l > 1e-6) {
      dx /= l;
      dz /= l;
    } else {
      dx = 0;
      dz = 1;
    }
    const k = this.tune.knockback;
    this.vx[i] = dx * k;
    this.vz[i] = dz * k;
    this.state[i] = STATE.HURT;
    this.timer[i] = 0;
    return false;
  }

  /**
   * Starts the death shrink. The slot frees itself `tune.dyingTime` later, from
   * inside `update` — so a kill during iteration never invalidates an index.
   *
   * THE single choke point for "this unit died", which is why {@link onKill}
   * fires here and not at the damage sites: the hero's auto-fire, a squad
   * melee swing, a ranged shot and a scripted `kill()` all funnel through it.
   * @param {number} i
   * @param {number} [src=-1] Attacker faction — see {@link damage}. Readable
   *   from {@link onKill} as {@link lastAttacker}.
   */
  kill(i, src = -1) {
    if (!this.isLive(i) || this.state[i] === STATE.DYING) return;
    this.lastAttacker = src;
    this.state[i] = STATE.DYING;
    this.timer[i] = 0;
    this.vx[i] = 0;
    this.vz[i] = 0;
    // Stops colliding and stops jostling the crowd the moment it dies.
    this.solid[i] = 0;
    // A dead unit is nobody's target and nobody's ally — drop the leash so a
    // recycled slot can never inherit a formation position.
    this.target[i] = -1;
    this.hasHome[i] = 0;
    this.onKill?.(i, this);
  }

  /**
   * Immediate removal, no death animation — the spawner's distance cull.
   * @param {number} i
   */
  despawn(i) {
    if (!this.isLive(i)) return;
    this._release(i);
  }

  /**
   * @param {number} i
   * @returns {boolean} Slot is occupied (DYING included).
   */
  isLive(i) {
    return i >= 0 && i < this.cap && this.state[i] !== STATE.DEAD;
  }

  /**
   * @param {(i: number, m: EnemyManager) => void} fn Called for every live slot,
   *   in slot order. Safe to `kill()` from inside; `spawn()` from inside may or
   *   may not be visited this pass.
   */
  forEachAlive(fn) {
    for (let i = 0; i < this.top; i++) {
      if (this.state[i] !== STATE.DEAD) fn(i, this);
    }
  }

  /** Frees every slot (level teardown, respawn). Fires `onDespawn` for each. */
  clear() {
    for (let i = 0; i < this.top; i++) {
      if (this.state[i] !== STATE.DEAD) this._release(i);
    }
    this.top = 0;
  }

  // ── brains: the pedestrian / seated API (faction mode) ────────────────────

  /**
   * Gives a slot a {@link BRAIN}. `CIVILIAN` takes it out of the fighter step
   * and into {@link _stepPed}; `SEATED` takes it out of the simulation entirely
   * (its transform is written from outside every step) and out of separation,
   * because a passenger must not shove the crowd through the car door.
   *
   * @param {number} i
   * @param {number} brain {@link BRAIN}
   */
  setBrain(i, brain) {
    if (!this.isLive(i)) return;
    this.brain[i] = brain & 255;
    if (brain !== BRAIN.SEATED) this.inCabin[i] = 0;
    if (brain === BRAIN.SEATED) {
      this.solid[i] = 0;
      this.vx[i] = 0;
      this.vz[i] = 0;
      this.wobbleAmp[i] = 0;
      this.wobbleTilt[i] = 0;
      this.wobbleBob[i] = 0;
      this.hasGoal[i] = 0;
    } else if (this.state[i] !== STATE.DYING) {
      this.solid[i] = 1;
    }
  }

  /**
   * Writes a strolling civilian's next waypoint. The manager steers at it and
   * nothing more — what a pavement is, and which waypoint comes next, belongs
   * to `systems/Population.js`.
   * @param {number} i @param {number} x @param {number} z
   */
  setGoal(i, x, z) {
    if (!this.isLive(i)) return;
    this.goalX[i] = x;
    this.goalZ[i] = z;
    this.hasGoal[i] = 1;
  }

  /**
   * @param {number} i
   * @returns {boolean} True when slot `i` wants a new waypoint: it has none, or
   *   it is standing on the one it had. A FLEEING civilian always says true —
   *   it is not walking a pavement and must not be handed one until it calms.
   */
  needsGoal(i) {
    if (!this.isLive(i) || this.brain[i] !== BRAIN.CIVILIAN) return false;
    if (this.mood[i] === PED.FLEE) return false;
    if (!this.hasGoal[i]) return true;
    const dx = this.goalX[i] - this.x[i];
    const dz = this.goalZ[i] - this.z[i];
    return dx * dx + dz * dz <= PED_TUNING.goalReach * PED_TUNING.goalReach;
  }

  /**
   * Frightens a civilian: it drops what it was doing and RUNS, away from
   * (`sx`, `sz`), for {@link PED_TUNING}`.fleeTime` seconds. Re-scaring refreshes
   * the timer and re-aims the bearing, which is what keeps a crowd running for
   * as long as the shooting lasts rather than for exactly six seconds after the
   * first bang.
   *
   * A no-op on anything that is not a live civilian, so a population director
   * can shout at a whole neighbourhood without filtering first.
   *
   * @param {number} i
   * @param {number} sx @param {number} sz The thing that scared it.
   * @returns {boolean} True when this slot actually took fright.
   */
  scare(i, sx, sz) {
    if (!this.isLive(i) || this.brain[i] !== BRAIN.CIVILIAN) return false;
    if (this.state[i] === STATE.DYING) return false;
    let dx = this.x[i] - sx;
    let dz = this.z[i] - sz;
    if (dx * dx + dz * dz < 1e-6) {
      // Scared by something standing exactly on top of it (or by a caller with
      // no direction to give): fall back to "away from the player", then to the
      // bearing it already had. Never a random draw — this runs off events.
      dx = this.x[i] - this._px;
      dz = this.z[i] - this._pz;
    }
    if (dx * dx + dz * dz > 1e-6) this.fleeA[i] = Math.atan2(dx, dz);
    this.mood[i] = PED.FLEE;
    this.moodT[i] = 0;
    this.hasGoal[i] = 0;
    return true;
  }

  /**
   * Slaves a SEATED actor to a transform computed elsewhere (a car's seat
   * anchor). Cheap enough to call for every occupant every step, which is
   * exactly how `Population` uses it.
   * ⚠ `inCabin` is what takes the actor OFF SCREEN. A caller slaving somebody
   * to a seat (`Population`'s seat loop) leaves it true and nothing is drawn
   * for them; a caller animating a body ACROSS the sill (the carjack drag)
   * passes false and the actor is drawn normally, which is the only way a man
   * being pulled out of a car can read as a man. Same call, same slot, one
   * argument apart.
   *
   * @param {number} i
   * @param {number} x @param {number} y @param {number} z
   * @param {number} yaw Radians, the project's `atan2(dx, dz)` convention.
   * @param {boolean} [inCabin=true] False once the body is out of the car.
   */
  placeSeated(i, x, y, z, yaw, inCabin = true) {
    if (!this.isLive(i) || this.brain[i] !== BRAIN.SEATED) return;
    this.x[i] = x;
    this.y[i] = y;
    this.z[i] = z;
    this.facing[i] = quantYaw(yaw);
    this.inCabin[i] = inCabin ? 1 : 0;
  }

  // ── the step ───────────────────────────────────────────────────────────────

  /**
   * ONE fixed step for the whole population.
   *
   * Order matters: rebuild the hash from LAST step's positions, then run the
   * FSM + steering + integration in one pass. Reading a half-updated hash would
   * make separation depend on slot order.
   *
   * ⚠ The `'player'` path through this method is FROZEN. Every teams-mode branch
   * below is gated on a local `teams` flag that hero mode cannot set, and the
   * arithmetic on the hero path is byte-for-byte what it was before squad mode
   * existed — including which rng numbers get drawn, and in what order.
   *
   * @param {number} dt Seconds (the engine's fixed step).
   * @param {EnemyUpdateCtx} [ctx]
   */
  update(dt, ctx = {}) {
    if (this._disposed || !(dt > 0) || this.top === 0) return;

    this.elapsed += dt;

    const tune = this.tune;
    const W = this.wobble;
    const rng = ctx.rng ?? this.rng;
    const factions = ctx.targeting === 'factions';
    // Factions ARE teams plus a bigger hostility test — every teams-mode branch
    // below is wanted, so one flag turns them all on.
    const teams = factions || ctx.targeting === 'teams';
    this._hostileFn = factions ? ctx.hostile ?? null : null;
    const playerHostile = factions ? ctx.playerHostile ?? null : null;
    const player = ctx.player ?? null;
    const px = player?.position?.x ?? 0;
    const py = player?.position?.y ?? 0;
    const pz = player?.position?.z ?? 0;
    const playerR = player?.radius ?? 0.35;
    this._px = px;
    this._pz = pz;

    const grid = ctx.collision ?? null;
    const canResolve = !!grid && typeof grid.resolveCircle === 'function';
    const heightAt = typeof ctx.heightAt === 'function' ? ctx.heightAt : null;
    const bus = ctx.bus ?? null;

    // 1. spatial hash over last step's positions.
    this._hash.build(this.top, this.x, this.z, this.solid);

    // 2. rolling probe refresh (a slice of the population per step).
    if (ctx.probes) this._refreshProbes(ctx.probes, py);

    // 3. rolling target refresh — same trick as the probes, and for the same
    //    reason: nearest-opponent is a linear scan, so amortise it over a window
    //    instead of paying O(n²) every step. Deaths still re-pick immediately
    //    (below), which is the only case a player can actually see.
    if (this._hostileFn) this._refreshFoeCensus();
    if (teams) this._refreshTargets();

    const fsm = this._fsm;
    const dir = this._seek;
    const home = this._seekHome;
    const push = this._push;

    for (let i = 0; i < this.top; i++) {
      const st = this.state[i];
      if (st === STATE.DEAD) continue;

      // ── brains ─────────────────────────────────────────────────────────────
      // Gated on the ARRAY, not on the mode: `brain` is zero for every hero
      // spawn and for every level that predates the population director, so the
      // frozen fighter path below is untouched by construction.
      const brain = this.brain[i];
      if (brain !== BRAIN.NONE) {
        if (brain === BRAIN.SEATED) this._stepSeated(i, dt);
        else this._stepPed(i, dt, rng, grid, canResolve, heightAt, W);
        continue;
      }

      const t = this.types[this.typeIdx[i]] ?? TYPE_FALLBACK;
      // `speedMul` is 1 for every spawn, and `x * 1` is bit-identical — see the
      // field's note. The hero path's arithmetic is unchanged.
      const maxSpeed = (t.speed ?? TYPE_FALLBACK.speed) * this.speedMul[i];
      let reach = playerR + this.radius[i] + tune.reachPad;

      // Squad-mode locals. Kept out of the hero path entirely: `teams` is false
      // there, so `fire` is null and none of the rest is read.
      /** @type {import('../types.js').FireSpec|null} */
      const fire = teams ? t.fire ?? null : null;
      let tgt = -1;
      let dTarget = Infinity;
      let aimX = 0;
      let aimZ = 0;
      let planted = false;
      /** FACTION MODE: this unit is fighting the PLAYER, not a slot. */
      let vsPlayer = false;

      // A type may override the global aggro band (a slow tank sees further).
      // A unit that can SHOOT `fire.range` metres necessarily notices that far
      // too, or it would stand in wander while an enemy it can hit walks past.
      let aggro = t.aggroRange > 0 ? t.aggroRange : tune.aggro;
      if (fire && fire.range > aggro) aggro = fire.range;
      const localTune = aggro === tune.aggro ? tune : this._localTune(aggro);

      // ── FSM ────────────────────────────────────────────────────────────────
      if (!teams) {
        seek(this.x[i], this.z[i], px, pz, dir);
        stepFSM(st, this.timer[i], this.dwell[i], dir.d, reach, dt, localTune, fsm);
      } else {
        // A corpse picks no fights: skip the scan entirely while it shrinks.
        tgt = st === STATE.DYING ? -1 : this._validTarget(i);

        if (tgt >= 0) {
          seek(this.x[i], this.z[i], this.x[tgt], this.z[tgt], dir);
          dTarget = dir.d;
          reach = this.radius[tgt] + this.radius[i] + tune.reachPad;
        } else {
          dir.x = 0;
          dir.z = 0;
          dir.d = Infinity;
        }

        // FACTION MODE: the player is a candidate too, and the NEARER of the
        // two wins. Picking the slot unconditionally would let a gang member
        // with a rival across the district ignore the man who just shot him.
        if (factions && player && st !== STATE.DYING && playerHostile?.(this.team[i])) {
          const pdx = px - this.x[i];
          const pdz = pz - this.z[i];
          if (pdx * pdx + pdz * pdz < dTarget * dTarget) {
            seek(this.x[i], this.z[i], px, pz, dir);
            dTarget = dir.d;
            tgt = -1;
            vsPlayer = true;
            reach = playerR + this.radius[i] + tune.reachPad;
          }
        }

        // Saved AFTER the pick and BEFORE `dir` may be re-aimed at a formation
        // slot: melee knockback and ranged shots both push away from the
        // SHOOTER, not away from home.
        aimX = dir.x;
        aimZ = dir.z;
        // A ranged unit must never reach ATTACK; a negative reach makes
        // `dist <= reach` unsatisfiable in both machines. It plants and shoots
        // instead — see the fire block below.
        if (fire) reach = -1;

        if (this.hasHome[i]) {
          seek(this.x[i], this.z[i], this.homeX[i], this.homeZ[i], home);
          const ret = leashCheck(this.returning[i] !== 0, home.d, tune);
          this.returning[i] = ret ? 1 : 0;
          if (ret) {
            // Broke the leash. Let go of the fight completely — keeping the
            // target would leave a ranged unit sniping over its shoulder all the
            // way back to the line, which reads as "the leash did nothing".
            this.target[i] = -1;
            tgt = -1;
          }
          // Hysteresis on the aggro band, matching `stepFSM`'s: already fighting
          // => hold on out to `deAggro`, otherwise commit only inside `aggro`.
          const band = st === STATE.CHASE || st === STATE.ATTACK || st === STATE.HURT
            ? localTune.deAggro
            : localTune.aggro;
          const engaged = tgt >= 0 && dTarget <= band;
          stepHomeFSM(st, this.timer[i], dTarget, home.d, engaged, reach, dt, localTune, fsm);
          // Not engaged => steer at the slot, not at the enemy.
          if (!engaged) {
            dir.x = home.x;
            dir.z = home.z;
            dir.d = home.d;
          }
        } else {
          stepFSM(st, this.timer[i], this.dwell[i], dTarget, reach, dt, localTune, fsm);
        }

        // Ranged: close to `fire.range * 0.9`, then stop and shoot. The 0.9 is
        // hysteresis — planting exactly at the range edge makes a line of
        // shooters shuffle in and out of range every step.
        if (fire && (tgt >= 0 || vsPlayer) && fsm.state === STATE.CHASE && dTarget <= fire.range * 0.9) {
          planted = true;
        }
      }

      this.state[i] = fsm.state;
      this.timer[i] = fsm.timer;
      if (fsm.retarget) {
        this.wanderA[i] = wanderDir(rng, this.elapsed);
        this.dwell[i] = range(rng, 0.8, 2.6);
      }
      if (fsm.attack) {
        if (vsPlayer) {
          // A faction unit that closed on the PLAYER hits it the same way a
          // hero-mode enemy does — through the bus. Same payload, same channel;
          // the HUD and `Gameplay`'s health handler never learn that factions
          // exist.
          bus?.emit?.('player:damage', {
            amount: t.damage ?? TYPE_FALLBACK.damage,
            from: i,
            type: this.typeIdx[i],
          });
        } else if (teams) {
          // Unit vs unit: damage the SLOT, knocked back away from the swinger.
          // No bus emit — `'player:damage'` means the hero took a hit, and squad
          // mode has no hero. Kills surface through `onKill` instead. The
          // attacker's faction rides along so a stray swing can start a feud.
          if (tgt >= 0) this.damage(tgt, t.damage ?? TYPE_FALLBACK.damage, aimX, aimZ, this.team[i]);
        } else if (bus?.emit) {
          // Fresh payload on purpose — see the header note about bus subscribers.
          bus.emit('player:damage', { amount: t.damage ?? TYPE_FALLBACK.damage, from: i, type: this.typeIdx[i] });
        }
      }

      const next = fsm.state;
      if (next === STATE.DEAD) {
        this._release(i);
        continue;
      }

      // ── ranged auto-fire ───────────────────────────────────────────────────
      if (fire) {
        if (vsPlayer) this._stepFirePlayer(i, dTarget, aimX, aimZ, next, fire, dt, px, pz, bus);
        else this._stepFire(i, tgt, dTarget, aimX, aimZ, next, fire, dt);
      }

      // A unit that has committed to a shot STOPS FOR IT — see {@link STANCE}.
      // ⚠ THIS LINE MUST STAY *AFTER* THE DISPATCH ABOVE, not up beside the
      // other `planted` assignment: the dispatch is what SETS `fireHold`, so
      // reading it earlier would see LAST step's value and the halt would lag
      // the trigger by a frame. Here, the `vx *= 0.35` in the CHASE case below
      // damps on the very step the shot is committed, and `moving` stays false
      // so the walk wobble dies at the same instant. THAT is the telegraph, and
      // the telegraph is the whole visual of the feature.
      // ⚠ CLOSED ALTERNATIVE — do NOT add a second "cannot fire while moving"
      // speed gate on the shot itself. It is redundant BY CONSTRUCTION: the
      // commit sets `fireHold` on the same step the damping starts, and over a
      // brace of ~10–30 fixed steps `0.35^n <= 3e-5`, so the unit IS stopped
      // when the round leaves. A second, weaker expression of one rule is the
      // "two places computing affordability" bug this project records one file
      // over. Inert while `stance` is null: `fireHold` is all zeros.
      //   ⚠ AND THE READ IS GATED ON `stance`, NOT ONLY ON THE FIELD BEING
      // ZERO. `stance` is a live property, and only the stance branch of
      // `_stepFire` decays these two fields — so turning it OFF mid-brace used
      // to STRAND `fireHold` non-zero, and this line then damped that unit to
      // 0.35 FOR EVER: it read `PLANTED` while walking, with the stance off.
      // Found on `/play/weapons`, which is the only thing in the project that
      // toggles it live; `Gameplay` sets it once and never clears it, so no
      // shipped path could reach it. The gate makes the whole stance state
      // inert when off, which is the invariant the opt-in claims; `_stepFire`'s
      // frozen path clears the fields so switching back ON starts clean.
      if (fire && this.stance && this.fireHold[i] > 0) planted = true;

      // ── desired velocity ───────────────────────────────────────────────────
      let vx = this.vx[i];
      let vz = this.vz[i];
      let moving = false;

      switch (next) {
        case STATE.CHASE: {
          if (planted) {
            // In position at range: damp like ATTACK does, keep facing the
            // target (the facing block below reads CHASE as "look at `dir`").
            vx *= 0.35;
            vz *= 0.35;
            break;
          }
          const s = maxSpeed;
          // Direct velocity assignment (not a force): a top-down mob wants to
          // feel decisive, and the separation term below is what stops it from
          // reading as a rigid formation.
          vx = dir.x * s;
          vz = dir.z * s;
          moving = true;
          break;
        }
        case STATE.WANDER: {
          const s = maxSpeed * tune.wanderSpeed;
          vx = Math.sin(this.wanderA[i]) * s;
          vz = Math.cos(this.wanderA[i]) * s;
          moving = true;
          break;
        }
        case STATE.ATTACK:
          // Planted for the swing — the windup reads as a commitment.
          vx *= 0.35;
          vz *= 0.35;
          break;
        case STATE.HURT:
          // Knockback decays fast; the stagger is 0.18 s of not steering.
          vx *= 0.86;
          vz *= 0.86;
          break;
        case STATE.DYING:
          vx = 0;
          vz = 0;
          break;
        default: // IDLE
          vx *= 0.8;
          vz *= 0.8;
          break;
      }

      // ── separation ─────────────────────────────────────────────────────────
      if (this.solid[i]) {
        const hits = this._hash.separationInto(i, this.x, this.z, this.radius, push);
        if (hits > 0) {
          const l = Math.hypot(push.x, push.z);
          if (l > tune.sepClamp) {
            push.x = (push.x / l) * tune.sepClamp;
            push.z = (push.z / l) * tune.sepClamp;
          }
          const k = tune.separation * maxSpeed;
          vx += push.x * k;
          vz += push.z * k;
        }
      }

      // ── integrate + resolve ────────────────────────────────────────────────
      let nx = this.x[i];
      let nz = this.z[i];
      const dxs = vx * dt;
      const dzs = vz * dt;
      if (dxs !== 0 || dzs !== 0) {
        nx += dxs;
        nz += dzs;
        if (canResolve && next !== STATE.DYING) {
          this._probePos.x = nx;
          this._probePos.z = nz;
          const r = grid.resolveCircle(this._probePos, this.radius[i], 1);
          if (r.hit) {
            const vn = vx * r.nx + vz * r.nz;
            if (vn < 0) {
              vx -= r.nx * vn;
              vz -= r.nz * vn;
            }
          }
          nx = r.x;
          nz = r.z;
        }
      }

      this.x[i] = nx;
      this.z[i] = nz;
      this.vx[i] = vx;
      this.vz[i] = vz;
      this.y[i] = heightAt ? heightAt(nx, nz) : this.y[i];

      // ── facing (sticky) ────────────────────────────────────────────────────
      if (next === STATE.CHASE || next === STATE.ATTACK) {
        if (dir.d > 1e-4) this.facing[i] = quantYaw(Math.atan2(dir.x, dir.z));
      } else {
        const sp2 = vx * vx + vz * vz;
        if (sp2 > 0.04) this.facing[i] = quantYaw(Math.atan2(vx, vz));
      }

      // ── inlined WalkWobble ─────────────────────────────────────────────────
      const speed = Math.sqrt(vx * vx + vz * vz);
      const s01 = maxSpeed > 0 ? clamp(speed / maxSpeed, 0, 1) : 0;
      let amp = damp(this.wobbleAmp[i], moving ? s01 : 0, W.damp, dt);
      if (amp < 1e-4 && !moving) amp = 0;
      this.wobbleAmp[i] = amp;

      let phase = this.animPhase[i];
      if (moving) {
        phase += dt * W.freq * (1 - W.speedFreq + W.speedFreq * s01);
        if (phase > TAU) phase -= TAU;
        this.animPhase[i] = phase;
      }
      this.wobbleTilt[i] = Math.sin(phase) * W.tilt * amp;
      this.wobbleBob[i] = (0.5 - 0.5 * Math.cos(phase * 2)) * W.bob * amp;

      // ── flash + death shrink ───────────────────────────────────────────────
      if (this.flash[i] > 0) {
        this.flash[i] = Math.max(0, this.flash[i] - dt / this.flashDecay);
      }
      this.scale[i] = next === STATE.DYING
        ? Math.max(0, 1 - this.timer[i] / tune.dyingTime)
        : 1;
    }
  }

  // ── stats / teardown ───────────────────────────────────────────────────────

  /**
   * @returns {{alive: number, top: number, cap: number, byState: number[],
   *   hostile: number, squad: number}}
   */
  stats() {
    const byState = [0, 0, 0, 0, 0, 0, 0];
    let peds = 0;
    let seated = 0;
    let fleeing = 0;
    for (let i = 0; i < this.top; i++) {
      byState[this.state[i]]++;
      if (this.state[i] === STATE.DEAD) continue;
      if (this.brain[i] === BRAIN.CIVILIAN) {
        peds++;
        if (this.mood[i] === PED.FLEE) fleeing++;
      } else if (this.brain[i] === BRAIN.SEATED) seated++;
    }
    /** Live count per FACTION id — the same byte `team` carries. */
    const byTeam = Array.from(this.aliveByTeam.subarray(0, 8));
    return {
      alive: this._alive,
      top: this.top,
      cap: this.cap,
      byState,
      byTeam,
      peds,
      seated,
      fleeing,
      hostile: this.aliveByTeam[TEAM.HOSTILE],
      squad: this.aliveByTeam[TEAM.SQUAD],
    };
  }

  /** Idempotent. The manager owns no GPU resources; this just frees the slots. */
  dispose() {
    if (this._disposed) return;
    this.clear();
    this._disposed = true;
    this.onSpawn = null;
    this.onDespawn = null;
    this.onKill = null;
    this.onFire = null;
    this.onDamage = null;
    this._hostileFn = null;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /**
   * @param {number} i
   */
  _release(i) {
    this.state[i] = STATE.DEAD;
    this.solid[i] = 0;
    this.scale[i] = 0;
    this.vx[i] = 0;
    this.vz[i] = 0;
    this.flash[i] = 0;
    this.target[i] = -1;
    this.hasHome[i] = 0;
    this.returning[i] = 0;
    this.fireAcc[i] = 0;
    // Plain assignments, never an rng draw — the same rule the team and brain
    // blocks in `spawn()` state. A recycled slot must never inherit somebody
    // else's half-squeezed trigger, or its first step would fire a round it
    // never committed to.
    this.fireBrace[i] = 0;
    this.fireHold[i] = 0;
    // A freed slot is nobody's pedestrian and nobody's passenger. `spawn()`
    // resets these too; doing it here as well means a census taken between a
    // release and the next spawn can never see a ghost in a car seat.
    this.brain[i] = BRAIN.NONE;
    this.hasGoal[i] = 0;
    this.inCabin[i] = 0;
    this._free[this._freeN++] = i;
    this._alive--;
    this.aliveByType[this.typeIdx[i]]--;
    this.aliveByTeam[this.team[i]]--;
    this.onDespawn?.(i, this);
    // Shrink the high-water mark when the tail dies, so the renderers stop
    // scanning slots nobody will ever use again.
    while (this.top > 0 && this.state[this.top - 1] === STATE.DEAD) this.top--;
  }

  // ── teams: targeting + ranged fire (never reached in hero mode) ────────────

  /**
   * The current target of slot `i`, re-picked when the stored one is unusable.
   *
   * A target is unusable the moment it is DEAD, DYING or has somehow ended up on
   * the same team — waiting out the rolling window there would leave a unit
   * swinging at a corpse for a quarter of a second, which is the one targeting
   * artefact a player actually notices.
   *
   * ⚠ Known and accepted: a freed slot can be re-spawned onto the opposing team
   * within the same window, and a unit still pointing at that slot silently
   * inherits the new occupant. It is by definition a live opponent at roughly
   * the same place, and the rolling refresh corrects it within `retargetMs`.
   *
   * @param {number} i
   * @returns {number} Slot id, or `-1` when nobody is available.
   */
  _validTarget(i) {
    const H = this._hostileFn;
    const mine = this.team[i];
    const tgt = this.target[i];
    if (tgt >= 0) {
      const ts = this.state[tgt];
      const stillFoe = H ? H(mine, this.team[tgt]) : this.team[tgt] !== mine;
      if (ts !== STATE.DEAD && ts !== STATE.DYING && stillFoe) return tgt;
    }
    // Cheap gate before an O(top) scan: is there anything to look for at all?
    // `_alive` counts DYING units too, so this can over-report and cost one
    // wasted scan while a last opponent shrinks. That is the whole downside.
    // With a hostility function the two-sided subtraction is simply wrong (a
    // third, neutral faction is not an opponent), so the per-step census
    // answers instead — see {@link _refreshFoeCensus}.
    const foes = H ? this._foeAlive[mine] : this._alive - this.aliveByTeam[mine];
    if (foes <= 0) {
      this.target[i] = -1;
      return -1;
    }
    return this._acquire(i);
  }

  /**
   * Nearest LIVE opponent, by a straight scan over the pool.
   *
   * Not the spatial hash: that grid is 3 m cells and only ever looks at the 3x3
   * neighbourhood, which is right for separation and useless at a 20 m aggro
   * band. Widening it to cover aggro would visit ~50 buckets per unit — worse
   * than the scan it replaces, at 100-odd units.
   *
   * Ties break to the LOWEST slot id, so the pick is a pure function of the
   * arrays and two runs of one seed never diverge here.
   *
   * @param {number} i
   * @returns {number} Slot id, or `-1`.
   */
  _acquire(i) {
    const myTeam = this.team[i];
    const H = this._hostileFn;
    const x = this.x[i];
    const z = this.z[i];
    let best = -1;
    let bestD2 = Infinity;
    for (let j = 0; j < this.top; j++) {
      const s = this.state[j];
      if (s === STATE.DEAD || s === STATE.DYING) continue;
      // NOBODY HUNTS A CAR. A SEATED actor is riding in one, and once the
      // population director can seat a GANG member (a carjack has to be able to
      // pull the wrong man out of the wrong car) the hostility matrix stops
      // covering this on its own: a rival crew chasing a saloon down a
      // boulevard at 12 m/s is not a street fight, it is a bug report. The
      // PLAYER can still shoot one — that path is `Gameplay#pickTarget` and
      // does not come through here.
      if (this.brain[j] === BRAIN.SEATED) continue;
      if (H ? !H(myTeam, this.team[j]) : this.team[j] === myTeam) continue;
      const dx = this.x[j] - x;
      const dz = this.z[j] - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = j;
      }
    }
    this.target[i] = best;
    return best;
  }

  /**
   * Re-picks a SLICE of the population's targets, exactly like
   * {@link _refreshProbes} re-samples a slice of the probe tints — and for the
   * same reason. With the default `retargetMs = 250` that is `top / 15` scans a
   * step, so the amortised cost is `top² / 15` per step instead of `top²`.
   */
  _refreshTargets() {
    const per = Math.max(1, Math.ceil(this.top / this._retargetSteps));
    const H = this._hostileFn;
    for (let k = 0; k < per; k++) {
      if (this._targetCursor >= this.top) this._targetCursor = 0;
      const i = this._targetCursor++;
      const s = this.state[i];
      if (s === STATE.DEAD || s === STATE.DYING) continue;
      // A pedestrian or a passenger never picks a fight, and never appears in
      // anyone else's scan either (the hostility matrix has civilians neutral).
      if (this.brain[i] !== BRAIN.NONE) continue;
      const foes = H ? this._foeAlive[this.team[i]] : this._alive - this.aliveByTeam[this.team[i]];
      if (foes <= 0) continue;
      this._acquire(i);
    }
  }

  /**
   * Live OPPONENTS per faction, rebuilt once per step. With a hostility matrix
   * "is there anyone to fight" stops being one subtraction, and paying an
   * 8x8 fold per STEP is orders of magnitude cheaper than paying an O(pool)
   * scan per SLOT to find out there was nobody.
   *
   * Only the first 8 faction ids are folded: `game/factions.js` has five and the
   * team byte is a faction id there. A higher id would read zero and simply
   * scan, which is correct if slower.
   */
  _refreshFoeCensus() {
    const H = this._hostileFn;
    const F = this._foeAlive;
    for (let a = 0; a < 8; a++) {
      let n = 0;
      for (let b = 0; b < 8; b++) {
        if (this.aliveByTeam[b] > 0 && H(a, b)) n += this.aliveByTeam[b];
      }
      F[a] = n;
    }
  }

  /**
   * One step of a ranged unit's weapon. The manager resolves the shot ITSELF
   * (miss roll, then damage) and then calls {@link onFire} — there is no
   * projectile actor, and squad mode's tracers are a pure visual played after
   * the fact. Making the shot instantaneous is what keeps the whole system
   * testable in node; the travelling streak the player sees is
   * `render/Tracers.js` animating an outcome that already happened.
   *
   * With {@link stance} set, the cadence gains ONE beat: the cooldown COMMITS
   * the unit (which halts it, through `fireHold` and the `planted` branch in
   * `update`) and the round leaves `brace` seconds later. Leave `fire.range`
   * inside that window and it never leaves at all — see {@link STANCE}. With
   * `stance` null this method is byte-for-byte the shipped one.
   *
   * @param {number} i Shooter.
   * @param {number} tgt Target slot, or `-1`.
   * @param {number} dTarget Metres to it.
   * @param {number} aimX @param {number} aimZ Unit vector shooter -> target.
   * @param {number} state The shooter's state AFTER this step's transition.
   * @param {import('../types.js').FireSpec} fire
   * @param {number} dt
   */
  _stepFire(i, tgt, dTarget, aimX, aimZ, state, fire, dt) {
    const ready = tgt >= 0 && dTarget <= fire.range && state !== STATE.HURT && state !== STATE.DYING;
    const stance = this.stance;

    if (stance) {
      // ⚠ The hold decays FIRST, before the `ready` test, so it keeps running
      // for a unit that just lost its target — see {@link fireHold}.
      const hold = this.fireHold[i] - dt;
      this.fireHold[i] = hold > 0 ? hold : 0;

      if (!ready) {
        // ⚠⚠ THIS IS THE PLAYER'S KITING ADVANTAGE, AND IT IS ONE LINE: leave
        // `fire.range` (or stagger the shooter) during the brace and the round
        // NEVER LEAVES. The cooldown still banks below, as it always did.
        this.fireBrace[i] = 0;
        const accOut = this.fireAcc[i] + dt;
        this.fireAcc[i] = accOut > fire.interval ? fire.interval : accOut;
        return;
      }

      // ⚠ The accumulator keeps running THROUGH the brace. Skipping it here is
      // how the halt would silently become a DPS nerf: the period would grow to
      // `interval + brace` instead of staying `interval`.
      let acc = this.fireAcc[i] + dt;
      if (acc > fire.interval) acc = fire.interval;

      const braced = this.fireBrace[i];
      if (braced > 0) {
        const left = braced - dt;
        if (left > 0) {
          this.fireBrace[i] = left;
        } else {
          // The brace ran out: THE ROUND LEAVES NOW, from a unit that has been
          // standing still for it.
          this.fireBrace[i] = 0;
          this._resolveShot(i, tgt, dTarget, aimX, aimZ, fire);
        }
        this.fireAcc[i] = acc;
        return;
      }

      // Not committed: the cooldown decides whether this step COMMITS. No shot
      // leaves now — that is the point.
      if (acc >= fire.interval) {
        acc -= fire.interval;
        if (acc > fire.interval) acc = fire.interval;
        this._commitStance(i, fire, stance);
      }
      this.fireAcc[i] = acc;
      return;
    }

    // ── the frozen path: byte-for-byte what shipped before the stance ────────
    // ⚠ …except for this clear, which touches no shipped arithmetic and exists
    // so the stance can be switched OFF without leaving state behind. Only the
    // branch above decays these two, so without it a unit caught mid-brace
    // keeps a non-zero `fireHold` for the rest of its life. Both are already 0
    // on every slot that never braced, so the common case is one comparison.
    if (this.fireHold[i] !== 0 || this.fireBrace[i] !== 0) {
      this.fireHold[i] = 0;
      this.fireBrace[i] = 0;
    }
    if (!ready) {
      // Out of range or staggered: the weapon still finishes reloading, but it
      // banks at most ONE shot. Without the clamp a unit that spent a minute out
      // of range would empty a magazine the instant it closed.
      const acc = this.fireAcc[i] + dt;
      this.fireAcc[i] = acc > fire.interval ? fire.interval : acc;
      return;
    }
    let acc = this.fireAcc[i] + dt;
    if (acc >= fire.interval) {
      acc -= fire.interval;
      // One shot per step, whatever the interval — a `while` here would let a
      // sub-step interval unload the whole magazine in one frame.
      if (acc > fire.interval) acc = fire.interval;
      this._resolveShot(i, tgt, dTarget, aimX, aimZ, fire);
    }
    this.fireAcc[i] = acc;
  }

  /**
   * Commits slot `i` to a shot: it plants NOW and the round leaves `brace`
   * seconds later. THE ONE PLACE the stance's two durations are derived, and
   * they are derived from the WEAPON'S OWN INTERVAL — see {@link STANCE} for
   * why a tabled pair of seconds is a bug and not a simplification.
   *
   * @param {number} i
   * @param {import('../types.js').FireSpec} fire
   * @param {{braceFrac?: number, recoverFrac?: number}|boolean} stance
   */
  _commitStance(i, fire, stance) {
    // `fire` overrides the manager, the manager overrides the table. A bare
    // `stance: true` reads `undefined` for both and lands on the table, which
    // is why nothing here has to normalise the option first.
    const brace = fire.interval * (fire.braceFrac ?? stance.braceFrac ?? STANCE.braceFrac);
    const recover = fire.interval * (fire.recoverFrac ?? stance.recoverFrac ?? STANCE.recoverFrac);
    this.fireBrace[i] = brace;
    this.fireHold[i] = brace + recover;
  }

  /**
   * Resolves ONE round at a target SLOT: where it went, whether it hurt, and
   * the {@link onFire} report. Extracted so the frozen path and the stance path
   * cannot drift apart — the stance changes WHEN a round leaves, never what it
   * does when it does.
   *
   * @param {number} i Shooter.
   * @param {number} tgt Target slot.
   * @param {number} dTarget Metres to it.
   * @param {number} aimX @param {number} aimZ Unit vector shooter -> target.
   * @param {import('../types.js').FireSpec} fire
   */
  _resolveShot(i, tgt, dTarget, aimX, aimZ, fire) {
    // Resolve WHERE the round went before deciding whether it hurt. With
    // `accuracy` unset this is a pure assignment and draws nothing — see the
    // ctor option.
    const shot = this._shot;
    if (this.accuracy) {
      rollShot(this.aimRng, shot, this.x[i], this.z[i], this.x[tgt], this.z[tgt], fire.range, this.accuracy);
    } else {
      shot.hit = true;
      shot.x = this.x[tgt];
      shot.z = this.z[tgt];
      shot.dist = dTarget;
    }
    if (shot.hit) this.damage(tgt, fire.damage, aimX, aimZ, this.team[i]);
    this.onFire?.(i, tgt, this, shot);
  }

  /**
   * {@link _stepFire}'s twin for a unit shooting at the PLAYER (faction mode).
   * Identical cadence, identical miss roll, identical `onFire` report — the
   * only difference is where the damage goes: the player is not a slot, so it
   * lands on the bus exactly as a melee hit does.
   *
   * `onFire` is called with a target of `-1`; the tracer reads `shot.x/z`,
   * which is the player on a hit and the scatter point on a miss, so the
   * visual needs no special case.
   *
   * @param {number} i Shooter.
   * @param {number} dTarget Metres to the player.
   * @param {number} aimX @param {number} aimZ Unit vector shooter -> player.
   * @param {number} state The shooter's state AFTER this step's transition.
   * @param {import('../types.js').FireSpec} fire
   * @param {number} dt
   * @param {number} px @param {number} pz Player position.
   * @param {import('../types.js').EventBus|null} bus
   */
  _stepFirePlayer(i, dTarget, aimX, aimZ, state, fire, dt, px, pz, bus) {
    const ready = dTarget <= fire.range && state !== STATE.HURT && state !== STATE.DYING;
    const stance = this.stance;
    void aimX;
    void aimZ;

    // Symmetrical with {@link _stepFire} down to the comments there — the two
    // twins differ in WHERE the damage goes and in nothing else, and a stance
    // that applied to slot-vs-slot fights but not to the one the player is in
    // would be a feature the player never sees.
    if (stance) {
      const hold = this.fireHold[i] - dt;
      this.fireHold[i] = hold > 0 ? hold : 0;

      if (!ready) {
        // Step out of `fire.range` during the brace and the round never leaves.
        this.fireBrace[i] = 0;
        const accOut = this.fireAcc[i] + dt;
        this.fireAcc[i] = accOut > fire.interval ? fire.interval : accOut;
        return;
      }

      // The accumulator runs through the brace: phase-shifted, never thinned.
      let acc = this.fireAcc[i] + dt;
      if (acc > fire.interval) acc = fire.interval;

      const braced = this.fireBrace[i];
      if (braced > 0) {
        const left = braced - dt;
        if (left > 0) {
          this.fireBrace[i] = left;
        } else {
          this.fireBrace[i] = 0;
          this._resolvePlayerShot(i, dTarget, fire, px, pz, bus);
        }
        this.fireAcc[i] = acc;
        return;
      }

      if (acc >= fire.interval) {
        acc -= fire.interval;
        if (acc > fire.interval) acc = fire.interval;
        this._commitStance(i, fire, stance);
      }
      this.fireAcc[i] = acc;
      return;
    }

    // ── the frozen path ──────────────────────────────────────────────────────
    if (!ready) {
      const acc = this.fireAcc[i] + dt;
      this.fireAcc[i] = acc > fire.interval ? fire.interval : acc;
      return;
    }
    let acc = this.fireAcc[i] + dt;
    if (acc >= fire.interval) {
      acc -= fire.interval;
      if (acc > fire.interval) acc = fire.interval;
      this._resolvePlayerShot(i, dTarget, fire, px, pz, bus);
    }
    this.fireAcc[i] = acc;
  }

  /**
   * {@link _resolveShot}'s twin for a round aimed at the PLAYER: the player is
   * not a slot, so the damage lands on the bus exactly as a melee hit does.
   * `onFire` is called with a target of `-1`; the tracer reads `shot.x/z`,
   * which is the player on a hit and the scatter point on a miss.
   *
   * @param {number} i Shooter.
   * @param {number} dTarget Metres to the player.
   * @param {import('../types.js').FireSpec} fire
   * @param {number} px @param {number} pz Player position.
   * @param {import('../types.js').EventBus|null} bus
   */
  _resolvePlayerShot(i, dTarget, fire, px, pz, bus) {
    const shot = this._shot;
    if (this.accuracy) {
      rollShot(this.aimRng, shot, this.x[i], this.z[i], px, pz, fire.range, this.accuracy);
    } else {
      shot.hit = true;
      shot.x = px;
      shot.z = pz;
      shot.dist = dTarget;
    }
    if (shot.hit) {
      bus?.emit?.('player:damage', { amount: fire.damage, from: i, type: this.typeIdx[i] });
    }
    this.onFire?.(i, -1, this, shot);
  }

  // ── brains: the two alternative steps ─────────────────────────────────────

  /**
   * ONE fixed step of a CIVILIAN. It shares the fighter step's second half
   * verbatim — separation, the collision slide, the integration, the sticky
   * facing, the inlined walk wobble — and replaces only the first: instead of
   * an aggro band and an attack reach it has a MOOD and, when strolling, a
   * waypoint somebody else chose.
   *
   * What it deliberately does NOT have: a target, an attack, a wander heading.
   * A civilian is never anybody's opponent and never swings at anything, so
   * `damage` is 0 in every civilian def and this function has no branch that
   * could make that a lie.
   *
   * @param {number} i
   * @param {number} dt
   * @param {Rng} rng Dwell + flight jitter. NEVER the hero path's stream —
   *   nothing here runs unless a population director set `brain`.
   * @param {any} grid @param {boolean} canResolve
   * @param {((x: number, z: number) => number)|null} heightAt
   * @param {typeof WOBBLE} W
   */
  _stepPed(i, dt, rng, grid, canResolve, heightAt, W) {
    const tune = this.tune;
    const t = this.types[this.typeIdx[i]] ?? TYPE_FALLBACK;
    // The def's authored speed IS the run. A stroll is a fraction of it, which
    // is why a civilian never needs a second stat to know how fast it panics.
    // ⚠ The per-slot scale rides the RUN as well as the stroll, which is the
    // whole point for a chase target: `Missions.js` makes a runner by putting a
    // civilian brain on a fleeing actor and turning `speedMul` up.
    const base = (t.speed ?? TYPE_FALLBACK.speed) * this.speedMul[i];
    const runSpeed = base * PED_TUNING.fleeScale;
    const strollSpeed = base * PED_TUNING.strollScale;
    const st = this.state[i];

    // ── death shrink (identical arithmetic to the fighter path) ─────────────
    if (st === STATE.DYING) {
      const timer = this.timer[i] + dt;
      this.timer[i] = timer;
      this.vx[i] = 0;
      this.vz[i] = 0;
      if (timer >= tune.dyingTime) {
        this._release(i);
        return;
      }
      this.scale[i] = Math.max(0, 1 - timer / tune.dyingTime);
      if (this.flash[i] > 0) this.flash[i] = Math.max(0, this.flash[i] - dt / this.flashDecay);
      return;
    }

    // ── stagger ─────────────────────────────────────────────────────────────
    // `damage()` already put this slot into FLEE; HURT only owns the knockback.
    let staggering = false;
    if (st === STATE.HURT) {
      const timer = this.timer[i] + dt;
      if (timer >= tune.hurtTime) {
        this.state[i] = STATE.WANDER;
        this.timer[i] = 0;
      } else {
        this.timer[i] = timer;
        staggering = true;
      }
    }

    // ── mood ────────────────────────────────────────────────────────────────
    const wasMood = this.mood[i];
    const wasT = this.moodT[i];
    const out = stepPedFSM(wasMood, wasT, this.moodDwell[i], dt, this._ped);
    this.mood[i] = out.mood;
    this.moodT[i] = out.timer;
    if (out.retarget) {
      this.moodDwell[i] = out.mood === PED.PAUSE
        ? range(rng, PED_TUNING.pauseMin, PED_TUNING.pauseMax)
        : range(rng, PED_TUNING.strollMin, PED_TUNING.strollMax);
      // Calming down drops the flight bearing and asks for a pavement waypoint
      // again — a civilian that stopped running in the middle of a road has to
      // walk back to a kerb, not resume a waypoint it left 40 m ago.
      if (out.calm) this.hasGoal[i] = 0;
    }
    if (out.mood === PED.FLEE && wasMood === PED.FLEE) {
      // Re-aim on a fixed cadence, not every step: a bearing that jitters at
      // 60 Hz averages out to a straight line and reads as a robot.
      const r = PED_TUNING.fleeRefresh;
      if (Math.floor(out.timer / r) > Math.floor(wasT / r)) {
        this.fleeA[i] += (rng() * 2 - 1) * PED_TUNING.fleeJitter;
      }
    }

    // ── desired velocity ────────────────────────────────────────────────────
    let vx = this.vx[i];
    let vz = this.vz[i];
    let moving = false;
    let maxSpeed = strollSpeed;

    if (staggering) {
      vx *= 0.86;
      vz *= 0.86;
    } else if (out.mood === PED.FLEE) {
      maxSpeed = runSpeed;
      vx = Math.sin(this.fleeA[i]) * runSpeed;
      vz = Math.cos(this.fleeA[i]) * runSpeed;
      moving = true;
    } else if (out.mood === PED.STROLL && this.hasGoal[i]) {
      const dir = this._seek;
      seek(this.x[i], this.z[i], this.goalX[i], this.goalZ[i], dir);
      if (dir.d > 1e-4) {
        vx = dir.x * strollSpeed;
        vz = dir.z * strollSpeed;
        moving = true;
      } else {
        vx *= 0.8;
        vz *= 0.8;
      }
    } else {
      // PAUSE, or strolling with nowhere to go yet.
      vx *= 0.8;
      vz *= 0.8;
    }

    // ── separation ──────────────────────────────────────────────────────────
    if (this.solid[i]) {
      const push = this._push;
      const hits = this._hash.separationInto(i, this.x, this.z, this.radius, push);
      if (hits > 0) {
        const l = Math.hypot(push.x, push.z);
        if (l > tune.sepClamp) {
          push.x = (push.x / l) * tune.sepClamp;
          push.z = (push.z / l) * tune.sepClamp;
        }
        const k = tune.separation * maxSpeed;
        vx += push.x * k;
        vz += push.z * k;
      }
    }

    // ── integrate + resolve ─────────────────────────────────────────────────
    let nx = this.x[i];
    let nz = this.z[i];
    const dxs = vx * dt;
    const dzs = vz * dt;
    if (dxs !== 0 || dzs !== 0) {
      nx += dxs;
      nz += dzs;
      if (canResolve) {
        this._probePos.x = nx;
        this._probePos.z = nz;
        const r = grid.resolveCircle(this._probePos, this.radius[i], 1);
        if (r.hit) {
          const vn = vx * r.nx + vz * r.nz;
          if (vn < 0) {
            vx -= r.nx * vn;
            vz -= r.nz * vn;
          }
        }
        nx = r.x;
        nz = r.z;
      }
    }

    this.x[i] = nx;
    this.z[i] = nz;
    this.vx[i] = vx;
    this.vz[i] = vz;
    this.y[i] = heightAt ? heightAt(nx, nz) : this.y[i];

    const sp2 = vx * vx + vz * vz;
    if (sp2 > 0.04) this.facing[i] = quantYaw(Math.atan2(vx, vz));

    // ── inlined WalkWobble (same formulas as the fighter path) ──────────────
    const speed = Math.sqrt(sp2);
    const s01 = maxSpeed > 0 ? clamp(speed / maxSpeed, 0, 1) : 0;
    let amp = damp(this.wobbleAmp[i], moving ? s01 : 0, W.damp, dt);
    if (amp < 1e-4 && !moving) amp = 0;
    this.wobbleAmp[i] = amp;

    let phase = this.animPhase[i];
    if (moving) {
      phase += dt * W.freq * (1 - W.speedFreq + W.speedFreq * s01);
      if (phase > TAU) phase -= TAU;
      this.animPhase[i] = phase;
    }
    this.wobbleTilt[i] = Math.sin(phase) * W.tilt * amp;
    this.wobbleBob[i] = (0.5 - 0.5 * Math.cos(phase * 2)) * W.bob * amp;

    if (this.flash[i] > 0) this.flash[i] = Math.max(0, this.flash[i] - dt / this.flashDecay);
    this.scale[i] = 1;
  }

  /**
   * ONE fixed step of a SEATED actor: the death timer, the hit flash, and
   * nothing else. Its transform belongs to whatever it is riding in and arrives
   * through {@link placeSeated}; steering it here would fight that writer every
   * frame, and letting it separate would have a passenger shoving pedestrians
   * through the bodywork.
   * @param {number} i @param {number} dt
   */
  _stepSeated(i, dt) {
    const tune = this.tune;
    const st = this.state[i];

    if (st === STATE.DYING) {
      const timer = this.timer[i] + dt;
      this.timer[i] = timer;
      if (timer >= tune.dyingTime) {
        this._release(i);
        return;
      }
      this.scale[i] = Math.max(0, 1 - timer / tune.dyingTime);
      if (this.flash[i] > 0) this.flash[i] = Math.max(0, this.flash[i] - dt / this.flashDecay);
      return;
    }

    if (st === STATE.HURT) {
      const timer = this.timer[i] + dt;
      if (timer >= tune.hurtTime) {
        this.state[i] = STATE.IDLE;
        this.timer[i] = 0;
      } else {
        this.timer[i] = timer;
      }
    }

    // A hit imparts knockback; a belted passenger does not fly out of the car.
    this.vx[i] = 0;
    this.vz[i] = 0;
    this.wobbleAmp[i] = 0;
    this.wobbleTilt[i] = 0;
    this.wobbleBob[i] = 0;
    this.scale[i] = 1;
    if (this.flash[i] > 0) this.flash[i] = Math.max(0, this.flash[i] - dt / this.flashDecay);
  }

  /**
   * A per-type aggro override without allocating a tuning object every enemy
   * every step: one cached record, rewritten in place.
   * @param {number} aggro
   * @returns {AiTuning}
   */
  _localTune(aggro) {
    const c = (this._tuneScratch ||= { ...this.tune });
    // `deAggro` must stay above `aggro` or enemies flicker on the boundary.
    c.aggro = aggro;
    c.deAggro = Math.max(this.tune.deAggro, aggro + 8);
    return /** @type {AiTuning} */ (c);
  }

  /**
   * Refreshes a slice of the population's probe tints. With the default
   * `probeMs = 300` that is `top / 18` enemies per step — 7 trilinear samples a
   * step at 120 enemies, i.e. free.
   * @param {import('../types.js').LightProbeGrid} probes
   * @param {number} playerY Fallback height when a slot has no ground sample.
   */
  _refreshProbes(probes, playerY) {
    const per = Math.max(1, Math.ceil(this.top / this._probeSteps));
    const dayNight = typeof probes.sampleDayNight === 'function';
    const D = this._probeD;
    const N = this._probeN;

    for (let k = 0; k < per; k++) {
      if (this._probeCursor >= this.top) this._probeCursor = 0;
      const i = this._probeCursor++;
      if (this.state[i] === STATE.DEAD) continue;

      const t = this.types[this.typeIdx[i]] ?? TYPE_FALLBACK;
      const h = (t.height ?? t.worldHeight ?? TYPE_FALLBACK.height) * 0.55;
      if (dayNight) {
        probes.sampleDayNight(this.x[i], this.z[i], D, N);
      } else if (typeof probes.sample === 'function') {
        // `types.js` contract form: two calls give the two channels separately,
        // so the SHADER keeps doing the uNight crossfade (as it does for the
        // player) instead of us baking a stale mix into the instance buffer.
        probes.sample(this.x[i], this.y[i] + h, this.z[i], 0, /** @type {any} */ (COL));
        D[0] = COL.r;
        D[1] = COL.g;
        D[2] = COL.b;
        probes.sample(this.x[i], this.y[i] + h, this.z[i], 1, /** @type {any} */ (COL));
        N[0] = COL.r;
        N[1] = COL.g;
        N[2] = COL.b;
      } else {
        continue;
      }

      const o3 = i * 3;
      this.tintD[o3] = D[0];
      this.tintD[o3 + 1] = D[1];
      this.tintD[o3 + 2] = D[2];
      this.tintN[o3] = N[0];
      this.tintN[o3 + 1] = N[1];
      this.tintN[o3 + 2] = N[2];
    }
    void playerY;
  }
}

/** Scratch colour for the `types.js` probe form (never escapes this module). */
const COL = { r: 0, g: 0, b: 0, setRGB(r, g, b) { this.r = r; this.g = g; this.b = b; return this; } };

/**
 * @param {number} yaw Radians (any range).
 * @returns {number} 0..255 — 1/256 of a turn.
 */
function quantYaw(yaw) {
  const u = yaw / TAU;
  return (Math.round((u - Math.floor(u)) * 256) & 255) >>> 0;
}

export { STATE, AI_DEFAULTS, TEAM, BRAIN, PED };
